<template>
  <ItemRow :class="$style.row">
    <ItemWithLabel
      :column="columns[0]"
    >
      {{ itemData.displayName }}
    </ItemWithLabel>
    <ItemWithLabel
      :column="columns[1]"
    >
      {{ itemData.hostname }}
    </ItemWithLabel>
    <ItemWithLabel
      :column="columns[2]"
    >
      {{ serialNumberField }}
    </ItemWithLabel>
    <ItemWithLabel
      :class="$style.colLastContact"
      :column="columns[3]"
    >
      {{ lastContactField }}
    </ItemWithLabel>
    <ItemWithLabel
      :column="columns[4]"
    >
      <StatusBadge
        :class="$style.statusBadge"
        :status="itemData.status"
        :text="statusText"
      />
    </ItemWithLabel>
    <ItemWithLabel
      :class="$style.actionButton"
      :column="columns[5]"
    >
      <ButtonGroup>
        <Button
          v-if="showViewResultsButton"
          hasOutline
          text="View Results"
          type="secondary"
          @click="handleViewResultsClick()"
        />
        <Button
          v-if="showRemoveDeviceButton"
          hasOutline
          text="Un-queue Device"
          type="error"
          @click="handleRemoveDeviceClick()"
        />
      </ButtonGroup>
    </ItemWithLabel>
  </ItemRow>
</template>

<script>
import {
  Button,
  ButtonGroup,
  ItemRow,
  ItemWithLabel,
  StatusBadge,
} from '@jumpcloud/ui-components';
import { cloneDeep } from 'lodash';
import { commandDevicesStatusMap, commandDevicesStatusTypes } from 'client/Commands/constants/CommandDevicesStatuses';
import { mapActions, mapGetters } from 'vuex';
import { notify } from 'client/util';
import CommandResultDetailsModal from 'client/Commands/CommandResultDetailsModal.vue';
import ModalEventBus from 'components/Modal/ModalEventBus';
import UtilConstants, { ItemTypeConsts, StoreModuleMap } from 'client/util/Constants';
import moment from 'moment';

const commandResultsCollection = StoreModuleMap[ItemTypeConsts.commandResult];
const commandQueueCollection = StoreModuleMap[ItemTypeConsts.commandQueue];

const { dataNotAvailable } = UtilConstants;
const { error, success } = UtilConstants;

export default {
  name: 'CommandQueueDetailRow',

  components: {
    Button,
    ItemWithLabel,
    ItemRow,
    ButtonGroup,
    StatusBadge,
  },

  props: {
    columns: {
      type: Array,
      default: () => ([]),
    },
    itemData: {
      required: true,
      type: Object,
    },
  },

  computed: {
    ...mapGetters(commandResultsCollection, {
      getItemCommandResults: 'getItem',
    }),

    lastContactField() {
      const { lastContact } = this.itemData;
      return moment(lastContact).fromNow();
    },

    serialNumberField() {
      const { serialNumber } = this.itemData;
      if (!serialNumber || serialNumber === '') return dataNotAvailable;
      return serialNumber;
    },

    showRemoveDeviceButton() {
      return this.itemData.status === commandDevicesStatusTypes.pending;
    },

    showViewResultsButton() {
      return this.itemData.status === commandDevicesStatusTypes.failed
        || this.itemData.status === commandDevicesStatusTypes.timeout
        || this.itemData.status === commandDevicesStatusTypes.successful
        || this.itemData.status === commandDevicesStatusTypes.running;
    },

    statusText() {
      if (this.itemData.status) return commandDevicesStatusMap[this.itemData.status].label;
      return dataNotAvailable;
    },
  },

  methods: {
    ...mapActions(commandQueueCollection, [
      'fetchAllCollectionCustom',
      'resetCollection',
      'unbindSystem',
      'setIsFetching',
    ]),

    ...mapActions(commandResultsCollection, {
      fetchItemCommandResults: 'fetchItem',
    }),

    async handleRemoveDeviceClick() {
      this.unbindSystem(this.itemData.commandQueue.id)
        .then(count => {
          if (count) {
            notify(success, 'Successfully canceled queued command.');
          } else {
            notify(error, 'No commands were queued.');
          }
        })
        .catch(() => notify(error, 'An error occurred when trying to remove the device.'))
        .finally(async () => {
          this.setIsFetching(true);
          this.resetCollection();
          await this.fetchAllCollectionCustom(this.itemData.workflowInstanceId);
          this.setIsFetching(false);
        });
    },

    async handleViewResultsClick() {
      const itemId = this.itemData.commandResult._id;
      await this.fetchItemCommandResults(itemId);

      const fullCommandResult = cloneDeep(this.getItemCommandResults(itemId));

      fullCommandResult.exitCode = '';
      const { data } = fullCommandResult.response;
      if (data && Object.keys(data).length > 0) {
        fullCommandResult.exitCode = fullCommandResult.response.data.exitCode;
      }

      let isRunning = false;
      if (this.itemData.status === commandDevicesStatusTypes.running) {
        isRunning = true;
      }

      ModalEventBus.open({
        isLargeModal: true,
        component: CommandResultDetailsModal,
        componentProps: {
          command: fullCommandResult,
          isRunning,
        },
      });
    },
  },
};

</script>

<style module scoped>

.colLastContact {
  color: var(--jc-color-disabled-text);
}

.statusBadge {
  margin-right: 2em;
  width: 10em;
}

.actionButton {
  width: 13em;
}
</style>
